// Meta Pixel is production-only, like PostHog: in other environments nothing is
// loaded and the track helpers no-op, so dev/staging traffic never reaches Meta.
const enabled = import.meta.env.VITE_ENVIRONMENT === 'production'

type FbqFn = (...args: unknown[]) => void

type Fbq = FbqFn & {
  callMethod?: FbqFn
  loaded?: boolean
  push?: FbqFn
  queue: unknown[][]
  version?: string
}

declare global {
  // Window augmentation requires declaration merging, which only an interface does.
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    _fbq?: Fbq
    fbq?: Fbq
  }
}

const SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js'

// The pathname of the last PageView we reported. `onResolved` is not guaranteed to
// fire for the first route (it only emits on a pending -> idle transition), so the
// initial PageView is sent from init() and this guard keeps the router from
// reporting the same path twice.
let lastTrackedPath: string | undefined

// Hand-rolled equivalent of Meta's minified loader snippet: a queueing stub that
// fbevents.js drains once it has loaded (it looks for `fbq.push` and `fbq.queue`).
const installStub = () => {
  if (window.fbq) return

  const fbq = ((...args: unknown[]) => {
    if (fbq.callMethod) fbq.callMethod(...args)
    else fbq.queue.push(args)
  }) as Fbq

  fbq.push = fbq
  fbq.loaded = true
  fbq.queue = []
  fbq.version = '2.0'

  window.fbq = fbq
  window._fbq ??= fbq

  const script = document.createElement('script')
  script.async = true
  script.src = SCRIPT_SRC
  document.head.appendChild(script)
}

export const initMetaPixel = () => {
  const pixelId = import.meta.env.VITE_META_PIXEL_ID

  if (!enabled || !pixelId) return

  installStub()
  window.fbq?.('init', pixelId)
  trackMetaPageView(window.location.pathname)
}

export const trackMetaPageView = (path: string) => {
  if (!enabled || path === lastTrackedPath) return

  lastTrackedPath = path
  window.fbq?.('track', 'PageView')
}

const readCookie = (name: string): string | undefined => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))

  return match?.[1] ? decodeURIComponent(match[1]) : undefined
}

/**
 * The two Meta identifiers the backend cannot see.
 *
 * `_fbp` is the Pixel's own browser cookie; `_fbc` encodes the ad click that brought the
 * person here and is the single highest-value matching signal in the whole integration.
 * Both live in cookies on this origin, so a server-side Conversions API event has no way
 * to obtain them — they are forwarded on every API request instead.
 */
export const readMetaBrowserContext = (): { fbc?: string; fbp?: string; } => {
  if (!enabled) return {}

  const fbclid = new URLSearchParams(window.location.search).get('fbclid')

  return {
    // The Pixel writes `_fbc` itself when it sees an `fbclid`, but only once it has
    // loaded. On the very first request of a landing-from-an-ad session that race is
    // real, so the same value is reconstructed from the URL in Meta's documented format.
    fbc: readCookie('_fbc') ?? (fbclid ? `fb.1.${Date.now()}.${fbclid}` : undefined),
    fbp: readCookie('_fbp'),
  }
}

/**
 * Meta's `external_id`, hashed exactly the way the backend hashes it.
 *
 * Firebase UIDs are case-sensitive and mixed-case, while Meta's browser-side advanced
 * matching lowercases values before hashing them. Handing the Pixel a raw UID and the
 * Conversions API `sha256(uid)` would therefore produce two different hashes for one
 * person, with nothing anywhere reporting a problem — just a match rate that never adds
 * up. Both sides lowercase and hash explicitly, and send the finished digest, which Meta
 * recognises as already-hashed and leaves alone.
 */
const hashExternalId = async (userId: string): Promise<string> => {
  const bytes = new TextEncoder().encode(userId.trim().toLowerCase())
  const digest = await crypto.subtle.digest('SHA-256', bytes)

  return Array.from(new Uint8Array(digest))
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Attaches the signed-in user to the Pixel, so browser events carry the same identity the
 * backend sends. Re-initialising with advanced matching is Meta's documented way to do
 * this once a visitor becomes known.
 */
export const identifyMetaUser = async (userId: string) => {
  const pixelId = import.meta.env.VITE_META_PIXEL_ID
  if (!enabled || !pixelId) return

  window.fbq?.('init', pixelId, { external_id: await hashExternalId(userId) })
}
